'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { analytics } from '@lib/analytics/page-events';
import { claimGestureEntry } from '@lib/coveo/historyGesture';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { getMicrocopyValue } from '@lib/util/microcopy';
import { useSession } from 'next-auth/react';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TreeAccordion } from 'ui-components';
import { FacetAccordion } from '../../FacetAccordion';
import { getFacetTreeIdPrefix } from '../../facetTypes';
import { RESULT_SECTION_SELECTOR } from '../../utils/ownershipFilter.constants';
import { OwnershipFacetProps } from './OwnershipFacet.types';

/**
 * "Hide owned assets" toggle. Per ADR 001 this is NOT a Coveo filter: the
 * state lives in the `hideOwnership` query param (preserved by the search
 * parameter serializer as a non-Coveo param) and owned cards are hidden
 * client-side by the searchOwnership.css rule keyed off `data-hide-owned`.
 * The known trade-off — result counts and pagination don't shrink with the
 * hidden cards — is consciously carried from the CSR path.
 */
export const OwnershipFacet = ({
  variant = 'sidebar',
  className,
  isOpen,
  onToggle,
}: OwnershipFacetProps) => {
  const session = useSession();
  const isAuthenticated = session.status === 'authenticated';
  const searchParams = useSearchParams();
  const { searchLabelsMicrocopy, searchValuesMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
    searchValuesMicrocopy: MicrocopyItem[];
  }>();

  const title = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.ownership',
    'Ownership'
  );

  const label = getMicrocopyValue(
    searchValuesMicrocopy,
    'search-ui-values.hide-owned-assets',
    'Hide owned assets'
  );

  // Local state initialised from URL, updated on toggle
  const [checked, setChecked] = useState(
    () => searchParams?.get('hideOwnership') === 'true'
  );

  // Sync local state if URL changes externally (e.g. browser back/forward)
  useEffect(() => {
    const fromUrl = searchParams?.get('hideOwnership') === 'true';
    setChecked(fromUrl);
  }, [searchParams]);

  // Every path that clears the ownership filter (the breadcrumb chip, the
  // breadcrumbs Clear button) calls clearOwnershipFilter explicitly, and the
  // URL-sync effect above unchecks from the resulting pushState.

  // DOM-based hiding of owned assets. Gated on the session because the toggle
  // itself is signed-in only: a `hideOwnership` deep link must not mark the
  // result section for an anonymous visitor who cannot see or clear the filter.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const el = document.querySelector(
      RESULT_SECTION_SELECTOR
    ) as HTMLElement | null;
    if (!el) return;
    if (checked && isAuthenticated) {
      el.setAttribute('data-hide-owned', 'true');
    } else {
      el.removeAttribute('data-hide-owned');
    }
  }, [checked, isAuthenticated]);

  const treeItems = useMemo(() => [{ id: 'ownership', label }], [label]);

  const selectedIds = useMemo(() => (checked ? ['ownership'] : []), [checked]);

  const handleSelectMany = useCallback((_ids: string[], selected: boolean) => {
    if (typeof window === 'undefined') return;
    const currentUrl = new URL(window.location.href);
    analytics.pushFilterEvent({
      filter_action: selected ? 'select' : 'deselect',
      filter_action_category: 'ownership',
      filter_action_label: 'hide_owned_assets',
    });
    if (selected) {
      currentUrl.searchParams.set('hideOwnership', 'true');
    } else {
      currentUrl.searchParams.delete('hideOwnership');
    }
    window.history.pushState({}, '', currentUrl);
    claimGestureEntry();
    setChecked(selected);
    const el = document.querySelector(
      RESULT_SECTION_SELECTOR
    ) as HTMLElement | null;
    if (selected) {
      el?.setAttribute('data-hide-owned', 'true');
    } else {
      el?.removeAttribute('data-hide-owned');
    }
  }, []);

  if (!isAuthenticated) {
    return null;
  }

  const content = (
    <TreeAccordion
      idPrefix={getFacetTreeIdPrefix(variant)}
      items={treeItems}
      selectionMode="multiple"
      selectedIds={selectedIds}
      onSelectMany={handleSelectMany}
      isDarkTheme={variant === 'mobile'}
      size={variant === 'sidebar' ? 'default' : 'sm'}
    />
  );

  return (
    <FacetAccordion
      title={title}
      variant={variant === 'horizontal' ? 'sidebar' : variant}
      className={className}
      defaultOpen={variant !== 'drawer'}
      selectedCount={checked ? 1 : 0}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      {content}
    </FacetAccordion>
  );
};
